/**
 * ConsentToggleGroup — Renders all 7 permission toggles with cascade logic.
 *
 * Crisis sub-toggles (urgency, contact) are disabled when the parent
 * allowCrisisNotifications toggle is OFF.
 */

import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { ConsentToggleRow } from './ConsentToggleRow';
import type {
  PractitionerConsentRecord,
  ConsentField,
} from '@/hooks/usePractitionerConsent';

interface ConsentToggleGroupProps {
  consent: PractitionerConsentRecord['consent'];
  onConsentChange: (field: ConsentField, value: boolean) => Promise<void>;
  disabled?: boolean;
  isSaving?: boolean;
}

export const ConsentToggleGroup: React.FC<ConsentToggleGroupProps> = React.memo(
  function ConsentToggleGroup({ consent, onConsentChange, disabled = false, isSaving = false }) {
    const { t } = useTranslation('practitioner_consent');

    const allDisabled = disabled || isSaving;
    const crisisEnabled = consent.allowCrisisNotifications && !allDisabled;

    const handlers = useMemo(
      () => ({
        canViewHistory: (value: boolean) => onConsentChange('canViewHistory', value),
        canViewOriginalMessage: (value: boolean) => onConsentChange('canViewOriginalMessage', value),
        canChat: (value: boolean) => onConsentChange('canChat', value),
        requiresApprovalForResults: (value: boolean) => onConsentChange('requiresApprovalForResults', value),
        allowCrisisNotifications: (value: boolean) => onConsentChange('allowCrisisNotifications', value),
        crisisUrgencyLevel: (value: boolean) => onConsentChange('crisisUrgencyLevel', value),
        crisisContactMethod: (value: boolean) => onConsentChange('crisisContactMethod', value),
      }),
      [onConsentChange]
    );

    return (
      <View style={styles.container}>
        {/* 1. View analysis history */}
        <ConsentToggleRow
          label={t('toggle.view_history')}
          sublabel={t('toggle.view_history_sub')}
          value={consent.canViewHistory}
          onValueChange={handlers.canViewHistory}
          disabled={allDisabled}
          variant="default"
          inputType="switch"
          testID="toggle-view-history"
          showDivider
        />

        {/* 2. View original messages */}
        <ConsentToggleRow
          label={t('toggle.view_messages')}
          sublabel={t('toggle.view_messages_sub')}
          value={consent.canViewOriginalMessage}
          onValueChange={handlers.canViewOriginalMessage}
          disabled={allDisabled}
          variant="default"
          inputType="switch"
          testID="toggle-view-messages"
          showDivider
        />

        {/* 3. Allow comments */}
        <ConsentToggleRow
          label={t('toggle.allow_comments')}
          sublabel={t('toggle.allow_comments_sub')}
          value={consent.canChat}
          onValueChange={handlers.canChat}
          disabled={allDisabled}
          variant="default"
          inputType="switch"
          testID="toggle-allow-comments"
          showDivider
        />

        {/* 4. Require approval per analysis */}
        <ConsentToggleRow
          label={t('toggle.require_approval')}
          sublabel={t('toggle.require_approval_sub')}
          value={consent.requiresApprovalForResults}
          onValueChange={handlers.requiresApprovalForResults}
          disabled={allDisabled}
          variant="default"
          inputType="switch"
          testID="toggle-require-approval"
          showDivider
        />

        {/* 5. Crisis notifications (safety variant — green) */}
        <ConsentToggleRow
          label={t('toggle.crisis_notifications')}
          sublabel={t('toggle.crisis_notifications_sub')}
          value={consent.allowCrisisNotifications}
          onValueChange={handlers.allowCrisisNotifications}
          disabled={allDisabled}
          variant="safety"
          inputType="switch"
          testID="toggle-crisis-notifications"
          showDivider
        />

        {/* 6. Crisis urgency level — gated on parent */}
        <ConsentToggleRow
          label={t('toggle.crisis_urgency')}
          sublabel={
            crisisEnabled
              ? t('toggle.crisis_urgency_sub')
              : t('toggle.crisis_urgency_sub_disabled')
          }
          value={consent.crisisUrgencyLevel !== null}
          onValueChange={handlers.crisisUrgencyLevel}
          disabled={!crisisEnabled}
          variant="default"
          inputType="switch"
          testID="toggle-crisis-urgency"
          showDivider
        />

        {/* 7. Contact method — always dash (future select sheet) */}
        <ConsentToggleRow
          label={t('toggle.crisis_contact')}
          sublabel={
            crisisEnabled
              ? t('toggle.crisis_contact_sub')
              : t('toggle.crisis_contact_sub_disabled')
          }
          value={false}
          onValueChange={handlers.crisisContactMethod}
          disabled={!crisisEnabled}
          variant="default"
          inputType="dash"
          testID="toggle-crisis-contact"
          showDivider={false}
        />
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    // No padding — rows handle their own padding
  },
});
